/**
 * RC Product EME - JavaScript functionality
 * EME Studios inspired product section
 */

(function() {
  'use strict';

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const sections = document.querySelectorAll('.rc-eme');
    sections.forEach(initSection);
  }

  /**
   * Lleva al selector de tallas, lo resalta y pone el foco en la primera talla disponible.
   * Lo usan el enlace de la galería y la barra fija de móvil.
   */
  function revealSizePicker(section) {
    const picker = section.querySelector('[data-size-picker-target]') || section.querySelector('.rc-eme__block--sizes');
    if (!picker) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Centra las tallas en la pantalla con un desplazamiento calculado (29-sep-2026): scrollIntoView no siempre se
    // movía y la barra fija de abajo tapaba las tallas.
    const pickerRect = picker.getBoundingClientRect();
    const targetTop = pickerRect.top + window.scrollY - Math.max(0, (window.innerHeight - pickerRect.height) / 2);
    window.scrollTo({ top: Math.max(0, targetTop), behavior: reduceMotion ? 'auto' : 'smooth' });

    picker.classList.remove('is-size-jump-highlight');
    void picker.offsetWidth;
    picker.classList.add('is-size-jump-highlight');
    window.setTimeout(() => picker.classList.remove('is-size-jump-highlight'), 1300);

    const firstAvailable = picker.querySelector('.rc-eme__size-input:not(:disabled)');
    if (firstAvailable) {
      window.setTimeout(() => firstAvailable.focus({ preventScroll: true }), reduceMotion ? 0 : 450);
    }
  }

  // 7-oct-2026 · Al intentar añadir sin talla, «Elige una talla» se pone en rojo y se anuncia, además de llevar a
  // las tallas (en el ordenador no había ninguna señal visible).
  function flagMissingSize(section) {
    revealSizePicker(section);
    const hint = section.querySelector('.rc-eme__selection-hint');
    if (!hint) return;
    hint.classList.add('is-error');
    hint.setAttribute('role', 'alert');
    const text = hint.textContent;
    hint.textContent = '';
    window.requestAnimationFrame(() => { hint.textContent = text; });
  }

  function initSection(section) {
    const sectionId = section.dataset.sectionId;
    const productId = section.dataset.productId;

    // Get product data
    const productJsonEl = document.getElementById(`rc-eme-product-json-${sectionId}`);
    if (!productJsonEl) return;

    const product = JSON.parse(productJsonEl.textContent);

    // 30-sep-2026 · Ya no se quitan los «,00» de los precios en euros al cargar: la ficha muestra €38,00 como las
    // tarjetas y la cesta (antes el precio cambiaba a «€38» medio segundo después de cargar).
    initMobileProductSummary(section);

    // Initialize components
    initGallery(section);
    initGallerySizeCta(section);
    initVariantPicker(section, product);
    initRestock(section);
    initSizeFinder(section);
    initQuantitySelector(section);
    initColorSwatches(section, product);
    initSizeChartModal(section);
    initMobileSizeJump(section);
    initStickyPurchaseBar(section);
    initCompleteTheLook(section);
    initAddToCart(section);
    initBuyNow(section);
    initDynamicBackground(section);
    initAccordion(section);
  }

  function initMobileProductSummary(section) {
    const summary = section.querySelector('[data-mobile-product-summary]');
    const form = section.querySelector('.rc-eme__form');
    const blocks = [
      form?.querySelector('.rc-eme__block--title'),
      form?.querySelector('.rc-eme__block--inventory'),
      form?.querySelector('.rc-eme__block--price')
    ].filter(Boolean);

    if (!summary || !form || blocks.length === 0 || summary.dataset.initialized === 'true') return;

    summary.dataset.initialized = 'true';
    const homeAnchor = document.createComment('rc-mobile-product-summary-home');
    blocks[0].before(homeAnchor);
    const mobileQuery = window.matchMedia('(max-width: 1007px)');

    const arrange = () => {
      const fragment = document.createDocumentFragment();
      blocks.forEach((block) => fragment.appendChild(block));

      if (mobileQuery.matches) {
        summary.appendChild(fragment);
      } else if (homeAnchor.parentNode) {
        homeAnchor.parentNode.insertBefore(fragment, homeAnchor.nextSibling);
      }
    };

    arrange();
    mobileQuery.addEventListener?.('change', arrange);
  }

  function initGallerySizeCta(section) {
    section.querySelectorAll('[data-gallery-size-cta]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const mobileTrigger = section.querySelector('[data-size-jump]');
        if (mobileTrigger && window.matchMedia('(max-width: 1007px)').matches) {
          mobileTrigger.click();
          return;
        }

        revealSizePicker(section);
      });
    });
  }

  function initSizeFinder(section) {
    const finder = section.querySelector('[data-rc-size-finder]');
    const openButton = section.querySelector('[data-rc-size-finder-open]');
    const mobileOpenButton = section.querySelector('[data-rc-size-finder-mobile-open]');
    if (!finder || !openButton) return;

    const closeButton = finder.querySelector('[data-rc-size-finder-close]');
    const submitButton = finder.querySelector('[data-rc-size-finder-submit]');
    const result = finder.querySelector('[data-rc-size-finder-result]');
    const heightInput = finder.querySelector('[data-rc-size-height]');
    const heightFeetInput = finder.querySelector('[data-rc-size-height-feet]');
    const heightInchesInput = finder.querySelector('[data-rc-size-height-inches]');
    const weightInput = finder.querySelector('[data-rc-size-weight]');
    const usualInput = finder.querySelector('[data-rc-size-usual]');
    const fitInput = finder.querySelector('[data-rc-size-fit]');
    const sizeInputs = Array.from(section.querySelectorAll('.rc-eme__size-input'));
    const form = section.querySelector('.rc-eme__form');
    const addButton = form?.querySelector('.rc-eme__add-to-cart');
    const price = section.querySelector('[data-product-price]');
    const isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
    const mobileSizePicker = section.querySelector('[data-mobile-size-picker]');
    const weightUnit = finder.dataset.weightUnit === 'lb' ? 'lb' : 'kg';

    if (window.matchMedia('(max-width: 1007px)').matches && mobileSizePicker) {
      mobileSizePicker.appendChild(finder);
    }

    const setOpen = (open) => {
      finder.hidden = !open;
      openButton.setAttribute('aria-expanded', String(open));
      mobileOpenButton?.setAttribute('aria-expanded', String(open));
      finder.classList.toggle('is-open', open);
    };

    openButton.addEventListener('click', () => setOpen(finder.hidden));
    mobileOpenButton?.addEventListener('click', () => setOpen(finder.hidden));
    closeButton?.addEventListener('click', () => setOpen(false));

    submitButton?.addEventListener('click', () => {
      const heightFeet = Number(heightFeetInput?.value);
      const heightInches = Number(heightInchesInput?.value);
      const height = heightFeetInput
        ? ((heightFeet * 12) + heightInches) * 2.54
        : Number(heightInput?.value);
      const enteredWeight = Number(weightInput?.value);
      const usual = usualInput?.value;
      const minWeight = weightUnit === 'lb' ? 88 : 40;
      const maxWeight = weightUnit === 'lb' ? 353 : 160;
      const imperialHeightIsValid = !heightFeetInput || (
        heightFeet >= 4 &&
        heightFeet <= 6 &&
        heightInches >= 0 &&
        heightInches <= 11
      );

      if (
        !height ||
        !enteredWeight ||
        !usual ||
        !imperialHeightIsValid ||
        height < 140 ||
        height > 210 ||
        enteredWeight < minWeight ||
        enteredWeight > maxWeight
      ) {
        result.textContent = isSpanish
          ? 'Completa altura, peso y talla habitual para calcularla.'
          : 'Add your height, weight and usual size to calculate it.';
        result.classList.add('is-error');
        return;
      }

      const sizes = sizeInputs.map((input) => input.value);
      let index = sizes.indexOf(usual);
      if (index < 0) index = Math.floor((sizes.length - 1) / 2);

      const weightKg = weightUnit === 'lb' ? enteredWeight / 2.2046226218 : enteredWeight;
      const bmi = weightKg / Math.pow(height / 100, 2);
      const bodyBias = bmi >= 28 ? 1 : bmi <= 18.5 ? -1 : 0;
      const fitBias = Number(fitInput?.value || 0);
      const productBias = Number(finder.dataset.fitBias || 0);
      const combinedBias = Math.max(-1, Math.min(1, bodyBias + fitBias + productBias));
      let recommendationIndex = Math.max(0, Math.min(sizes.length - 1, index + combinedBias));

      if (sizeInputs[recommendationIndex]?.dataset.available === 'false') {
        const availableIndexes = sizeInputs
          .map((input, inputIndex) => input.dataset.available === 'false' ? -1 : inputIndex)
          .filter((inputIndex) => inputIndex >= 0)
          .sort((a, b) => Math.abs(a - recommendationIndex) - Math.abs(b - recommendationIndex));
        if (availableIndexes.length) recommendationIndex = availableIndexes[0];
      }

      const recommendedInput = sizeInputs[recommendationIndex];
      const recommendedSize = recommendedInput?.value;
      if (!recommendedSize || recommendedInput.dataset.available === 'false') {
        result.textContent = isSpanish
          ? 'Ahora mismo no hay una talla cercana disponible.'
          : 'There is no close size available right now.';
        result.classList.add('is-error');
        return;
      }

      recommendedInput.checked = true;
      recommendedInput.dispatchEvent(new Event('change', { bubbles: true }));

      const fitLabel = fitInput?.options[fitInput.selectedIndex]?.textContent.trim() || '';
      const explanation = isSpanish
        ? `Según tu talla habitual (${usual}) y un corte ${fitLabel.toLowerCase()}.`
        : `Based on your usual size ${usual} and ${fitLabel.toLowerCase()} fit.`;
      const directAddLabel = isSpanish
        ? `Añadir talla ${recommendedSize} · ${price?.textContent.trim() || ''}`
        : `Add size ${recommendedSize} · ${price?.textContent.trim() || ''}`;

      result.classList.remove('is-error');
      result.innerHTML = `
        <span class="rc-eme__size-result-copy">
          <small>${isSpanish ? 'Talla recomendada' : 'Recommended size'}</small>
          <strong>${recommendedSize}</strong>
          <span>${explanation}</span>
        </span>
        <button type="button" data-rc-size-add>${directAddLabel}</button>
      `;

      document.dispatchEvent(new CustomEvent('racecode:size-recommendation', {
        detail: { recommended_size: recommendedSize, usual_size: usual }
      }));
      window.RaceCodeAnalytics?.track('rc_size_recommendation', {
        product_id: section.dataset.productId,
        recommended_size: recommendedSize,
        usual_size: usual,
        preferred_fit: fitInput?.value
      });

      result.querySelector('[data-rc-size-add]')?.addEventListener('click', () => {
        if (!form || !addButton || addButton.disabled) return;
        form.requestSubmit(addButton);
      }, { once: true });
    });
  }

  /**
   * Mobile inline size picker
   */
  function initMobileSizeJump(section) {
    const trigger = section.querySelector('[data-size-jump]');
    const picker = section.querySelector('[data-mobile-size-picker]');
    const options = picker?.querySelector('[data-mobile-size-options]');
    const mobileSizeButtons = picker?.querySelectorAll('[data-mobile-size-value]') || [];
    const realSizeInputs = section.querySelectorAll('.rc-eme__size-input');
    const form = section.querySelector('.rc-eme__form');
    const addButton = form?.querySelector('.rc-eme__add-to-cart');
    if (!picker || !options || !mobileSizeButtons.length || !realSizeInputs.length || !form || !addButton) return;

    if (trigger) {
      trigger.addEventListener('click', () => {
        picker.classList.add('is-open');
        options.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');

        picker.querySelector('[data-mobile-size-value]:not(:disabled)')?.focus({ preventScroll: true });
      });
    }

    const syncMobileSelection = (value) => {
      mobileSizeButtons.forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.mobileSizeValue === value ? 'true' : 'false');
      });
    };

    mobileSizeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const matchingInput = Array.from(realSizeInputs).find((input) => input.value === button.dataset.mobileSizeValue);
        if (!matchingInput) return;

        matchingInput.checked = true;
        matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
        syncMobileSelection(matchingInput.value);
      });
    });

    realSizeInputs.forEach((input) => {
      input.addEventListener('change', () => syncMobileSelection(input.value));
    });

    syncMobileSelection(section.querySelector('.rc-eme__size-input:checked')?.value || '');
  }

  /**
   * Mobile sticky purchase bar
   */
  function initStickyPurchaseBar(section) {
    const stickyBar = section.querySelector('[data-sticky-buy]');
    const stickyButton = stickyBar?.querySelector('[data-sticky-buy-button]');
    const stickySize = stickyBar?.querySelector('[data-sticky-buy-size]');
    const stickyPrice = stickyBar?.querySelector('[data-sticky-buy-price]');
    const form = section.querySelector('.rc-eme__form');
    const addButton = form?.querySelector('.rc-eme__add-to-cart');
    const mainAnchor = section.querySelector('.rc-eme__mobile-size-cta') || form?.querySelector('.rc-eme__block--buy');
    const price = section.querySelector('[data-product-price]');
    const mobileQuery = window.matchMedia('(max-width: 1007px)');
    const phoneQuery = window.matchMedia('(max-width: 749px)');

    if (!stickyBar || !stickyButton || !form || !addButton || !mainAnchor) return;

    const requiresSize = stickyBar.dataset.requiresSize === 'true';
    let scrollFrame = 0;

    const syncContent = () => {
      const checkedSize = form.querySelector('.rc-eme__size-input:checked');
      const hasSelectedSize = !requiresSize || Boolean(checkedSize);

      if (stickySize) {
        // «Elige una talla» ya lo dice el botón: aquí solo la talla elegida (29-sep-2026).
        stickySize.textContent = checkedSize ? `${checkedSize.value} · ` : '';
      }

      if (stickyPrice && price) {
        stickyPrice.textContent = price.textContent.trim();
      }

      stickyButton.disabled = hasSelectedSize ? addButton.disabled : false;
      stickyButton.textContent = hasSelectedSize
        ? (addButton.textContent.trim() || stickyButton.dataset.addText)
        : stickyButton.dataset.selectSizeText;
    };

    const syncVisibility = () => {
      scrollFrame = 0;

      if (!mobileQuery.matches) {
        stickyBar.classList.remove('is-visible');
        stickyBar.setAttribute('aria-hidden', 'true');
        stickyBar.inert = true;
        return;
      }

      const anchorRect = mainAnchor.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      // En el móvil la barra también se ve antes de llegar al botón principal, como en Nude Project
      // (29-sep-2026): así «Añadir» está a mano desde la primera pantalla.
      const anchorBelowView = phoneQuery.matches && anchorRect.top > window.innerHeight;
      const shouldShow = (anchorRect.bottom < 0 || anchorBelowView) && sectionRect.bottom > 120;
      stickyBar.classList.toggle('is-visible', shouldShow);
      stickyBar.setAttribute('aria-hidden', String(!shouldShow));
      // Oculta también su botón al teclado y a los lectores de pantalla mientras no se ve.
      stickyBar.inert = !shouldShow;
    };

    const requestVisibilitySync = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(syncVisibility);
    };

    stickyButton.addEventListener('click', () => {
      const checkedSize = form.querySelector('.rc-eme__size-input:checked');

      if (requiresSize && !checkedSize) {
        const mobileTrigger = section.querySelector('[data-size-jump]');
        if (mobileTrigger) {
          mobileTrigger.click();
        } else {
          flagMissingSize(section);
        }
        return;
      }

      if (!addButton.disabled) {
        form.requestSubmit(addButton);
      }
    });

    form.querySelectorAll('.rc-eme__size-input').forEach((input) => {
      input.addEventListener('change', syncContent);
    });

    if (window.MutationObserver) {
      new MutationObserver(syncContent).observe(addButton, {
        attributes: true,
        attributeFilter: ['disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      if (price) {
        new MutationObserver(syncContent).observe(price, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    window.addEventListener('scroll', requestVisibilitySync, { passive: true });
    window.addEventListener('resize', requestVisibilitySync, { passive: true });
    mobileQuery.addEventListener?.('change', syncVisibility);
    syncContent();
    syncVisibility();
  }

  /**
   * Barra fina «Talla M · Añadido a la cesta · Ver cesta» (30-sep-2026). Se crea una vez, bajo la cabecera fija,
   * se anuncia a los lectores de pantalla y se va sola a los 5 s (se queda mientras tienes el dedo o el ratón encima).
   */
  let addedBar = null;
  let addedBarTimer = 0;

  function hideAddedBar() {
    window.clearTimeout(addedBarTimer);
    addedBar?.classList.remove('is-visible');
  }

  function showAddedBar(size) {
    const isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
    if (!addedBar) {
      addedBar = document.createElement('div');
      addedBar.className = 'rc-added-bar';
      addedBar.setAttribute('role', 'status');
      addedBar.setAttribute('aria-live', 'polite');
      const text = document.createElement('span');
      text.className = 'rc-added-bar__text';
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'rc-added-bar__link';
      link.addEventListener('click', () => {
        hideAddedBar();
        if (window.RaceCodeCart?.open) {
          window.RaceCodeCart.open();
        } else {
          window.location.href = `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}cart`;
        }
      });
      addedBar.append(text, link);
      addedBar.addEventListener('pointerenter', () => window.clearTimeout(addedBarTimer));
      addedBar.addEventListener('pointerleave', () => {
        window.clearTimeout(addedBarTimer);
        addedBarTimer = window.setTimeout(hideAddedBar, 2500);
      });
      document.body.appendChild(addedBar);
    }

    const sizeText = size ? (isSpanish ? `Talla ${size} · ` : `Size ${size} · `) : '';
    addedBar.querySelector('.rc-added-bar__text').textContent = sizeText + (isSpanish ? 'Añadido a la cesta' : 'Added to cart');
    addedBar.querySelector('.rc-added-bar__link').textContent = isSpanish ? 'Ver cesta' : 'View cart';

    const header = document.querySelector('.rc-header');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    addedBar.style.setProperty('--rc-added-bar-top', `${top}px`);
    addedBar.classList.remove('is-visible');
    void addedBar.offsetWidth;
    addedBar.classList.add('is-visible');

    window.clearTimeout(addedBarTimer);
    addedBarTimer = window.setTimeout(hideAddedBar, 5000);
  }

  /**
   * Add to Cart Handler
   */
  function initAddToCart(section) {
    const form = section.querySelector('.rc-eme__form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const addToCartBtn = form.querySelector('.rc-eme__add-to-cart');
      const variantId = form.querySelector('[name="id"]')?.value;
      const quantity = form.querySelector('[name="quantity"]')?.value || 1;
      const addSource = 'product_page';

      if (!variantId && section.classList.contains('rc-eme--simple')) {
        // Sin talla elegida: el botón sigue activo y lleva a las tallas (29-sep-2026).
        flagMissingSize(section);
        return;
      }

      if (!variantId || addToCartBtn?.disabled) {
        console.error('No variant ID found');
        return;
      }

      // Show loading state
      const originalText = addToCartBtn?.textContent;
      const isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('is-loading');
        addToCartBtn.textContent = isSpanish ? 'Añadiendo…' : 'Adding…';
      }

      try {
        // Collect line item properties from any properties[...] hidden inputs
        const properties = {};
        form.querySelectorAll('input[name^="properties["]').forEach((input) => {
          const m = input.name.match(/^properties\[(.+)\]$/);
          if (!m) return;
          const key = m[1];
          const val = (input.value || '').trim();
          if (val) properties[key] = val;
        });

        // Add to cart (gestionada: la cesta no se abre sola; la actualizamos y enseñamos la barra fina)
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-RaceCode-Cart-Managed': 'true',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: parseInt(quantity, 10),
            ...(Object.keys(properties).length ? { properties } : {})
          })
        });

        const responseData = await response.json();

        if (response.ok) {
          // Fetch updated cart to get item count
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();

          // 30-sep-2026 · Sin «cart:update»: con «auto-open» ese evento abre la cesta. Contador de la cabecera a mano;
          // la cesta se vuelve a pintar abajo con RaceCodeCart.refresh.
          document.querySelectorAll('.rc-header__cart-count, [data-cart-count]').forEach((el) => {
            el.textContent = String(cartData.item_count);
          });
          document.dispatchEvent(new CustomEvent('racecode:add-to-cart', {
            detail: {
              source: addSource,
              product_id: responseData.product_id,
              variant_id: responseData.variant_id || responseData.id,
              quantity: parseInt(quantity, 10)
            }
          }));

          if (addToCartBtn) {
            addToCartBtn.classList.remove('is-loading');
            addToCartBtn.classList.add('is-added');
            addToCartBtn.textContent = isSpanish ? '✓ Añadido a la cesta' : '✓ Added to cart';
            setTimeout(() => {
              addToCartBtn.classList.remove('is-added');
              addToCartBtn.textContent = originalText;
              addToCartBtn.disabled = false;
            }, 1500);
          }

          // 30-sep-2026 · Como Nude Project: no se abre la cesta; barra fina bajo la cabecera con la talla y «Ver cesta».
          window.RaceCodeCart?.refresh?.(false);
          const addedSize = form.querySelector('.rc-eme__size-input:checked')?.value || '';
          showAddedBar(addedSize);
        } else {
          console.error('Add to cart error:', responseData);
          document.dispatchEvent(new CustomEvent('racecode:ui-error', {
            detail: {
              component: 'product_form',
              error: responseData.description || 'add_to_cart_failed'
            }
          }));
          if (addToCartBtn) {
            addToCartBtn.classList.remove('is-loading');
            addToCartBtn.textContent = responseData.description || 'Error';
            setTimeout(() => {
              addToCartBtn.textContent = originalText;
              addToCartBtn.disabled = false;
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        document.dispatchEvent(new CustomEvent('racecode:ui-error', {
          detail: {
            component: 'product_form',
            error: error?.message || 'add_to_cart_failed'
          }
        }));
        if (addToCartBtn) {
          addToCartBtn.classList.remove('is-loading');
          addToCartBtn.textContent = (document.documentElement.lang || '').startsWith('es') ? 'No se ha podido añadir' : 'Could not add';
          setTimeout(() => {
            addToCartBtn.textContent = originalText;
            addToCartBtn.disabled = false;
          }, 2000);
        }
      }
    });
  }

  /**
   * Secondary direct-to-checkout action.
   * It only becomes available once a valid variant has been selected.
   */
  function initBuyNow(section) {
    const form = section.querySelector('.rc-eme__form');
    const buyNowButton = form?.querySelector('[data-rc-buy-now]');
    const variantIdInput = form?.querySelector('[name="id"]');
    const sizeInputs = form?.querySelectorAll('.rc-eme__size-input') || [];
    const addToCartButton = form?.querySelector('.rc-eme__add-to-cart');
    if (!form || !buyNowButton || !variantIdInput || !addToCartButton) return;

    const syncState = () => {
      const hasVariant = Boolean(variantIdInput.value);
      const isAvailable = hasVariant && !addToCartButton.disabled;
      buyNowButton.hidden = !isAvailable;
      buyNowButton.disabled = !isAvailable;
    };

    sizeInputs.forEach((input) => input.addEventListener('change', syncState));

    if (window.MutationObserver) {
      new MutationObserver(syncState).observe(addToCartButton, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    buyNowButton.addEventListener('click', async () => {
      const variantId = variantIdInput.value;
      if (!variantId || buyNowButton.disabled) return;

      const quantity = form.querySelector('[name="quantity"]')?.value || 1;
      const properties = {};
      form.querySelectorAll('input[name^="properties["]').forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        const value = (input.value || '').trim();
        if (match && value) properties[match[1]] = value;
      });

      buyNowButton.disabled = true;
      buyNowButton.classList.add('is-loading');
      buyNowButton.textContent = buyNowButton.dataset.loadingText;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: parseInt(quantity, 10),
            ...(Object.keys(properties).length ? { properties } : {})
          })
        });

        if (!response.ok) throw new Error('direct_checkout_failed');

        window.RaceCodeAnalytics?.track('rc_buy_now', {
          product_id: section.dataset.productId,
          variant_id: variantId,
          quantity: parseInt(quantity, 10)
        });
        window.location.assign('/checkout');
      } catch (error) {
        buyNowButton.classList.remove('is-loading');
        buyNowButton.textContent = buyNowButton.dataset.errorText;
        window.setTimeout(() => {
          buyNowButton.textContent = buyNowButton.dataset.defaultText;
          syncState();
        }, 1800);
      }
    });

    syncState();
  }

  /**
   * Gallery / Slideshow
   */
  function initGallery(section) {
    const slides = section.querySelector('.rc-eme__slides');
    const slideItems = section.querySelectorAll('.rc-eme__slide');
    const dots = section.querySelectorAll('.rc-eme__dot');
    const thumbnails = section.querySelectorAll('.rc-eme__thumbnail');
    const carouselNavItems = section.querySelectorAll('.rc-eme__carousel-nav-item');

    if (!slides || slideItems.length === 0) return;

    let currentIndex = 0;

    // Handle scroll on mobile
    slides.addEventListener('scroll', () => {
      const scrollLeft = slides.scrollLeft;
      const slideWidth = slides.offsetWidth;
      const newIndex = Math.round(scrollLeft / slideWidth);

      if (newIndex !== currentIndex) {
        currentIndex = newIndex;
        updateActiveStates(currentIndex);
      }
    });

    // Dot click
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        goToSlide(index);
      });
    });

    // Thumbnail click (desktop)
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.index, 10);
        scrollToSlideDesktop(index);
        updateActiveStates(index);
      });
    });

    // Carousel nav click (mobile)
    carouselNavItems.forEach(item => {
      item.addEventListener('click', () => {
        const index = parseInt(item.dataset.index, 10);
        goToSlide(index);
      });
    });

    function goToSlide(index) {
      const slideWidth = slides.offsetWidth;
      slides.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
      currentIndex = index;
      updateActiveStates(index);
    }

    function scrollToSlideDesktop(index) {
      const slide = slideItems[index];
      if (slide) {
        slide.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function updateActiveStates(index) {
      // Update dots
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });

      // Update thumbnails
      thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });

      // Update carousel nav
      carouselNavItems.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
      });

      // Update slides
      slideItems.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
    }

    // Desktop: Track scroll position for thumbnails
    if (window.innerWidth >= 1008) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const index = parseInt(entry.target.dataset.index, 10);
            updateActiveStates(index);
          }
        });
      }, {
        threshold: 0.5
      });

      slideItems.forEach(slide => observer.observe(slide));
    }
  }

  /**
   * Variant Picker
   */
  function initVariantPicker(section, product) {
  const form = section.querySelector('.rc-eme__form');
  if (!form) return;
  const variantIdInput = form.querySelector('#rc-eme-variant-id');
  if (!variantIdInput) return;
  const sizeInputs = Array.from(form.querySelectorAll('.rc-eme__size-input'));
  const addToCartBtn = form.querySelector('.rc-eme__add-to-cart');
  const buyBlock = form.querySelector('.rc-eme__block--buy');
  const priceEl = section.querySelector('[data-product-price]');
  const dynamicCheckout = form.querySelector('[data-rc-dynamic-checkout]');
  const initialButtonText = addToCartBtn?.textContent || '';
  const initialPriceText = priceEl?.textContent || '';
  const variants = Array.isArray(product.variants) ? product.variants : [];

  function applyVariant(variant, updateUrl) {
    if (!variant) return;
    variantIdInput.value = String(variant.id);
    if (section.classList.contains('rc-eme--simple')) variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    sizeInputs.forEach(input => {
      input.checked = input.dataset.variantId === String(variant.id)
        || input.value === variant.options?.[variant.options.length - 1];
    });
    section.querySelectorAll('.rc-eme__color-swatch[data-option-value]').forEach(swatch => {
      swatch.classList.toggle('is-active', variant.options?.includes(swatch.dataset.optionValue));
    });
    const selectedSize = sizeInputs.find(input => input.checked)?.value || '';
    section.querySelectorAll('[data-mobile-size-value]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.mobileSizeValue === selectedSize));
    });
    let formattedPrice = '';
    if (priceEl) {
      formattedPrice = formatMoney(variant.price, priceEl.dataset.moneyFormat, shouldStripTrailingZeroDecimals(priceEl));
      priceEl.textContent = formattedPrice;
    }
    if (addToCartBtn) {
      addToCartBtn.disabled = !variant.available;
      addToCartBtn.textContent = variant.available
        ? (section.classList.contains('rc-eme--simple') ? addToCartBtn.dataset.addText : `${addToCartBtn.dataset.addText} · ${formattedPrice || priceEl?.textContent.trim() || ''}`)
        : addToCartBtn.dataset.soldOutText;
    }
    if (buyBlock) buyBlock.dataset.awaitingSize = 'false';
    const sizeHint = section.querySelector('.rc-eme__selection-hint');
    if (sizeHint) {
      sizeHint.classList.remove('is-error');
      sizeHint.removeAttribute('role');
    }
    if (dynamicCheckout) {
      dynamicCheckout.hidden = !variant.available;
      if (!variant.available && dynamicCheckout instanceof HTMLDetailsElement) dynamicCheckout.open = false;
    }
    section.dispatchEvent(new CustomEvent('racecode:variant-change', { detail: { variant }, bubbles: false }));
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }
  }

  function clearSelection() {
    variantIdInput.value = '';
    if (section.classList.contains('rc-eme--simple')) variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    sizeInputs.forEach(input => { input.checked = false; });
    section.querySelectorAll('[data-mobile-size-value]').forEach(button => button.setAttribute('aria-pressed', 'false'));
    if (addToCartBtn) {
      // 14-oct-2026 · Producto no disponible en el país del visitante (rc-region-blocked): el botón sigue desactivado
      addToCartBtn.disabled = addToCartBtn.dataset.regionBlocked === 'true' || !section.classList.contains('rc-eme--simple');
      addToCartBtn.textContent = initialButtonText;
    }
    if (priceEl) priceEl.textContent = initialPriceText;
    if (buyBlock) buyBlock.dataset.awaitingSize = 'true';
    if (dynamicCheckout) {
      dynamicCheckout.hidden = true;
      if (dynamicCheckout instanceof HTMLDetailsElement) dynamicCheckout.open = false;
    }
    const unavailable = section.querySelector('[data-rc-bis]');
    if (unavailable) unavailable.hidden = true;
    const restock = section.querySelector('[data-rc-restock]');
    if (restock) {
      restock.hidden = true;
      document.documentElement.classList.remove('rc-restock-open');
    }
  }

  function restoreFromUrl() {
    if (!section.isConnected) return;
    const url = new URL(window.location.href);
    const id = url.searchParams.get('variant');
    const selected = variants.find(variant => String(variant.id) === id);
    if (selected) applyVariant(selected, false);
    else if (variants.length === 1) applyVariant(variants[0], false);
    else clearSelection();
    if (id && !selected) {
      url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url);
    }
  }

  sizeInputs.forEach(input => input.addEventListener('change', () => {
    if (!input.checked) return;
    const selectedOptions = [];
    const activeColor = section.querySelector('.rc-eme__color-swatch.is-active[data-option-value]');
    if (activeColor) selectedOptions.push(activeColor.dataset.optionValue);
    selectedOptions.push(input.value);
    const selected = variants.find(variant => variant.options?.length === selectedOptions.length
      && selectedOptions.every((option, index) => variant.options[index] === option));
    if (selected) applyVariant(selected, true);
    else clearSelection();
  }));
  window.addEventListener('popstate', restoreFromUrl);
  window.addEventListener('pageshow', event => { if (event.persisted) restoreFromUrl(); });
  queueMicrotask(restoreFromUrl);
}

function initRestock(section) {
    const panel = section.querySelector('[data-rc-restock]');
    if (!panel) return;

    const openButton = section.querySelector('[data-rc-restock-open]');
    const unavailablePanel = section.querySelector('[data-rc-bis]');
    const closeButton = panel.querySelector('[data-rc-restock-close]');
    const emailInput = panel.querySelector('input[type="email"]');
    const selectedSize = panel.querySelector('[data-rc-restock-size]');
    const tagsInput = panel.querySelector('[data-rc-restock-tags]');
    const productHandle = panel.dataset.productHandle;
    let activeVariant = {
      id: panel.dataset.initialVariantId,
      title: panel.dataset.initialVariantTitle,
      available: panel.dataset.initialAvailable === 'true'
    };

    const closePanel = ({ restoreFocus = true } = {}) => {
      panel.hidden = true;
      document.documentElement.classList.remove('rc-restock-open');
      if (restoreFocus) openButton?.focus({ preventScroll: true });
    };

    const updateVariant = (variant) => {
      if (!variant) return;
      activeVariant = variant;
      if (unavailablePanel) unavailablePanel.hidden = Boolean(variant.available);

      if (variant.available) {
        closePanel({ restoreFocus: false });
        return;
      }

      const displaySize = variant.title && variant.title !== 'Default Title'
        ? variant.title
        : section.querySelector('.rc-eme__title')?.textContent.trim() || '';
      if (selectedSize) selectedSize.textContent = displaySize;
      if (tagsInput) {
        tagsInput.value = `restock-request,product-${productHandle},variant-${variant.id}`;
      }
    };

    const openPanel = () => {
      if (!activeVariant || activeVariant.available) return;
      panel.hidden = false;
      document.documentElement.classList.add('rc-restock-open');
      window.requestAnimationFrame(() => emailInput?.focus({ preventScroll: true }));
    };

    section.addEventListener('racecode:variant-change', (event) => {
      updateVariant(event.detail?.variant);
    });

    openButton?.addEventListener('click', openPanel);
    closeButton?.addEventListener('click', () => closePanel());
    panel.addEventListener('click', (event) => {
      if (event.target === panel) closePanel();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) closePanel();
    });

    updateVariant(activeVariant);

    if (panel.querySelector('[data-rc-restock-posted]')) {
      openPanel();
    }
  }

  /**
   * Color Swatches
   */
  function initColorSwatches(section, product) {
    const swatches = section.querySelectorAll('.rc-eme__color-swatch');

    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        // Update active state
        swatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        // Trigger variant update
        const sizeInput = section.querySelector('.rc-eme__size-input:checked');
        if (sizeInput) {
          sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update gallery to show variant image
        const variantId = swatch.dataset.variantId;
        const variant = product.variants.find(v => v.id === parseInt(variantId, 10));

        if (variant && variant.featured_image) {
          const imageId = variant.featured_image.id;
          const slides = section.querySelectorAll('.rc-eme__slide');

          slides.forEach((slide, index) => {
            const img = slide.querySelector('img');
            if (img && img.src.includes(variant.featured_image.src.split('?')[0].split('/').pop())) {
              // Scroll to this slide
              const slidesContainer = section.querySelector('.rc-eme__slides');
              if (slidesContainer && window.innerWidth < 1008) {
                slidesContainer.scrollTo({
                  left: slidesContainer.offsetWidth * index,
                  behavior: 'smooth'
                });
              }
            }
          });
        }
      });
    });
  }

  /**
   * Quantity Selector
   */
  function initQuantitySelector(section) {
    const quantityWrapper = section.querySelector('.rc-eme__quantity');
    if (!quantityWrapper) return;

    const input = quantityWrapper.querySelector('.rc-eme__quantity-input');
    const decreaseBtn = quantityWrapper.querySelector('[data-action="decrease"]');
    const increaseBtn = quantityWrapper.querySelector('[data-action="increase"]');

    if (!input) return;

    decreaseBtn?.addEventListener('click', () => {
      const currentValue = parseInt(input.value, 10) || 1;
      if (currentValue > 1) {
        input.value = currentValue - 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    increaseBtn?.addEventListener('click', () => {
      const currentValue = parseInt(input.value, 10) || 1;
      input.value = currentValue + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    input.addEventListener('change', () => {
      const value = parseInt(input.value, 10);
      if (isNaN(value) || value < 1) {
        input.value = 1;
      }
    });
  }

  /**
   * Format money (simple version)
   */
  function formatMoney(cents, format, withoutTrailingZeros) {
    // Prefer Shopify's money formatter; pass the shop money_format so it
    // matches the Liquid `| money` initial render exactly.
    var formatted;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      formatted = format ? Shopify.formatMoney(cents, format) : Shopify.formatMoney(cents);
      return withoutTrailingZeros ? stripTrailingZeroDecimals(formatted) : formatted;
    }
    // Local fallback that mirrors the Liquid money_format placeholders so the
    // displayed format stays identical when Shopify.formatMoney isn't loaded.
    function num(c, prec, ts, ds) {
      var n = (c / 100).toFixed(prec).split('.');
      var i = n[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + ts);
      return i + (n[1] ? ds + n[1] : '');
    }
    var fmt = format || '${{amount}}';
    var t = document.createElement('textarea');
    t.innerHTML = fmt;
    fmt = t.value;
    formatted = fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) {
      if (k === 'amount') return num(cents, 2, ',', '.');
      if (k === 'amount_no_decimals') return num(cents, 0, ',', '.');
      if (k === 'amount_with_comma_separator') return num(cents, 2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return num(cents, 0, '.', ',');
      if (k === 'amount_with_apostrophe_separator') return num(cents, 2, "'", '.');
      return num(cents, 2, ',', '.');
    });
    return withoutTrailingZeros ? stripTrailingZeroDecimals(formatted) : formatted;
  }

  function stripTrailingZeroDecimals(value) {
    return String(value).replace(/([,.]00)(?!\d)/g, '');
  }

  function shouldStripTrailingZeroDecimals(priceEl) {
    return priceEl.dataset.moneyWithoutTrailingZeros === 'true';
  }

  /**
   * Size Chart Modal
   */
  function initSizeChartModal(section) {
    const openBtns = section.querySelectorAll('[data-size-chart-open]');
    const modal = section.querySelector('[data-size-chart-modal]');

    if (!modal || openBtns.length === 0) return;

    const dialog = modal.querySelector('.rc-eme__size-chart-modal-inner');
    const closeBtns = modal.querySelectorAll('[data-size-chart-close]');
    const unitBtns = modal.querySelectorAll('.rc-eme__size-chart-unit');
    const cmTable = modal.querySelector('[data-unit-table="cm"]');
    const inchTable = modal.querySelector('[data-unit-table="inches"]');
    let lastActiveElement = null;

    // Open modal
    openBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModal(btn);
      });
    });

    // Close modal
    closeBtns.forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });

    // Unit toggle
    unitBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        setUnit(btn.dataset.unit);
      });
    });

    function setUnit(unit) {
      unitBtns.forEach(btn => {
        const isActive = btn.dataset.unit === unit;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', String(isActive));
      });

      if (cmTable) cmTable.hidden = unit !== 'cm';
      if (inchTable) inchTable.hidden = unit !== 'inches';
    }

    function openModal(trigger) {
      lastActiveElement = trigger || document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (dialog) dialog.focus({ preventScroll: true });
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
        lastActiveElement.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Complete the Look - Add to Cart on select change
   */
  function initCompleteTheLook(section) {
    const selects = section.querySelectorAll('[data-ctl-select]');

    selects.forEach(select => {
      select.addEventListener('change', async () => {
        const variantId = select.value;

        if (!variantId) return;

        // Show loading state
        select.disabled = true;

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: parseInt(variantId, 10),
              quantity: 1
            })
          });

          if (response.ok) {
            // Dispatch custom event for cart update
            document.dispatchEvent(new CustomEvent('cart:updated'));

            // Reset select to default
            select.selectedIndex = 0;
            select.disabled = false;
          } else {
            const errorData = await response.json();
            console.error('Add to cart error:', errorData);
            select.disabled = false;
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          select.disabled = false;
        }
      });
    });
  }

  /**
   * Dynamic Background Color - Extract dominant color from first slide image
   * Optimized: uses main slide image (already loaded), samples only corners,
   * and runs during idle time
   */
  function initDynamicBackground(section) {
    const wrapper = section.querySelector('.rc-eme__wrapper');
    // Use main slide image instead of thumbnail (already loaded, no extra fetch)
    const firstSlide = section.querySelector('.rc-eme__slide-image');

    if (!wrapper || !firstSlide) return;

    const imgSrc = firstSlide.src;
    if (!imgSrc) return;

    // Create image with CORS enabled
    const corsImage = new Image();
    corsImage.crossOrigin = 'anonymous';

    corsImage.onload = () => {
      // Use requestIdleCallback to run when browser is idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => extractAndApplyColor(corsImage, wrapper));
      } else {
        // Fallback for Safari
        setTimeout(() => extractAndApplyColor(corsImage, wrapper), 0);
      }
    };

    corsImage.onerror = () => {
      // Silently fail - keep default background
    };

    corsImage.src = imgSrc;
  }

  /**
   * Extract dominant color from left-center of image and apply to wrapper
   * Samples left-center area where background is typically visible
   */
  function extractAndApplyColor(img, wrapper) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const size = 50;
      canvas.width = size;
      canvas.height = size;
      ctx.drawImage(img, 0, 0, size, size);

      const imageData = ctx.getImageData(0, 0, size, size);
      const data = imageData.data;

      let r = 0, g = 0, b = 0, count = 0;

      // Sample left-center area (6x10 pixels = 60 pixels)
      // x: 0-5 (left edge), y: 20-29 (vertical center)
      const sampleWidth = 6;
      const sampleHeight = 10;
      const startX = 0;
      const startY = Math.floor((size - sampleHeight) / 2); // center vertically

      for (let y = startY; y < startY + sampleHeight; y++) {
        for (let x = startX; x < startX + sampleWidth; x++) {
          const i = (y * size + x) * 4;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          count++;
        }
      }

      // Disabled: keep fixed rgb(239 239 239) wrapper bg from CSS.
      // (Was sampling product image to colour-match wrapper.)
      void [r, g, b, count];
    } catch (e) {
      // CORS or other error - keep default color
    }
  }

  /**
   * Exclusive Accordion - only one <details> open at a time
   */
  function initAccordion(section) {
    const accordions = section.querySelectorAll('.rc-eme__accordion');
    if (accordions.length < 2) return;

    accordions.forEach(details => {
      const summary = details.querySelector('summary');

      summary?.addEventListener('click', (event) => {
        if (!window.matchMedia('(max-width: 1007px)').matches) return;

        event.preventDefault();
        const anchorTop = summary.getBoundingClientRect().top;
        details.open = !details.open;
        summary.focus({ preventScroll: true });

        const restoreSummaryPosition = () => {
          const positionShift = summary.getBoundingClientRect().top - anchorTop;
          if (Math.abs(positionShift) > 1) {
            window.scrollBy({ top: positionShift, behavior: 'auto' });
          }
        };

        window.requestAnimationFrame(() => {
          restoreSummaryPosition();
          window.requestAnimationFrame(restoreSummaryPosition);
          window.setTimeout(restoreSummaryPosition, 80);
        });
      });

      details.addEventListener('toggle', () => {
        if (details.open) {
          if (window.matchMedia('(max-width: 1007px)').matches) return;

          const anchorTop = summary?.getBoundingClientRect().top;

          accordions.forEach(other => {
            if (other !== details && other.open) {
              other.removeAttribute('open');
            }
          });

          if (summary && Number.isFinite(anchorTop)) {
            const positionShift = summary.getBoundingClientRect().top - anchorTop;
            if (Math.abs(positionShift) > 1) {
              window.scrollBy({ top: positionShift, behavior: 'auto' });
            }
          }
        }
      });
    });
  }

})();
